import { Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Grid2x2Plus } from 'lucide-react';
import { Fragment, useState } from 'react';
import { GridSize, MAX_COLS, MAX_ROWS, getGridSizeLabel } from '../../util';

interface Props {
  value: GridSize | null;
  onChange: (size: GridSize | null) => void;
}

const ROWS = Array.from({ length: MAX_ROWS }, (_, i) => i + 1);
const COLS = Array.from({ length: MAX_COLS }, (_, i) => i + 1);

export function GridSizePicker(props: Props) {
  const { value, onChange } = props;
  const [hover, setHover] = useState<GridSize | null>(null);

  const isCellActive = (row: number, col: number) => {
    const ref = hover ?? value;
    return !!ref && col <= ref.cols && row <= ref.rows;
  };

  return (
    <Popover className="relative">
      <Popover.Button className="text-onlight-text-1 dark:text-ondark-text-1 border-onlight-line-2 dark:border-ondark-line-2 flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm outline-none">
        <Grid2x2Plus className="mr-1 h-5 w-5 flex-none" />
        {getGridSizeLabel(value)}
        <ChevronDownIcon className="h-4 w-4" />
      </Popover.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Popover.Panel className="dark:bg-dark-bg absolute right-0 z-20 mt-2 rounded-md bg-white p-3 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:ring-gray-600">
          {({ close }) => (
            <div
              className="flex flex-col gap-2"
              onMouseLeave={() => setHover(null)}
            >
              <button
                onClick={() => {
                  onChange(null);
                  close();
                }}
                className={clsx(
                  'dark:text-dark-text rounded-md px-2 py-1 text-left text-sm text-gray-700',
                  value === null
                    ? 'bg-onlight-bg-3 dark:bg-ondark-bg-3'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
              >
                Auto
              </button>
              <div className="flex flex-col gap-1">
                {ROWS.map((row) => (
                  <div key={row} className="flex gap-1">
                    {COLS.map((col) => (
                      <button
                        key={col}
                        onMouseEnter={() => setHover({ rows: row, cols: col })}
                        onClick={() => {
                          onChange({ rows: row, cols: col });
                          close();
                        }}
                        className={clsx(
                          'h-5 w-5 rounded-sm border',
                          isCellActive(row, col)
                            ? 'border-primary-500 bg-primary-400/40'
                            : 'border-gray-300 dark:border-gray-600'
                        )}
                      />
                    ))}
                  </div>
                ))}
              </div>
              <div className="text-onlight-text-3 dark:text-ondark-text-2 text-center text-xs">
                {getGridSizeLabel(hover ?? value)}
              </div>
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}

export default GridSizePicker;
